import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { useApolloClient } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
  Select,
  Typography,
  Box,
  Paper,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';

import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ImportExportIcon from '@mui/icons-material/ImportExport';

import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'validation';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { exportToFile } from 'helper/download';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputDate } from 'components/form/FormInputDate';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormInputDropdown, buildMenuItemsFromOptions } from 'components/form/FormInputDropdown';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import CustomTabs from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';
import PagedTable from 'components/table/PagedTable';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { SpaceSelectionInput, buildSpaceSelectionOptions } from 'components/security/SpaceSelectionInput';
import { HotelSelectionInput } from 'components/security/HotelSelectionInput';
import { userSelector, canEditAdminRecord, canEditAdminSpaceId, initialSpaceId, filterForSelectableRecords } from 'helper/security';
import { filterSelector, bookingModeSelector } from 'helper/filter';
import { EBookingMode } from 'types';

import {
  PRICELIST_VIEW_QUERY,
  CREATE_PRICELIST_MUTATION,
  UPDATE_PRICELIST_MUTATION,
  DELETE_PRICELIST_MUTATION,
  COPY_PRICELIST_MUTATION,
  EXPORT_PRICES_MUTATION,
  REFETCH_PRICELISTS_QUERIES,
  EVICT_PRICELISTS_QUERIES,
  PRICESELECTOR_LIST_QUERY,
  PRICELISTS_LIST_QUERY,
  HOTEL_PRICELISTS_QUERY,
  HOTEL_WITH_SPACE_DETAILS_QUERY,
} from './gql';
import { HOTEL_LIST_QUERY } from '../settings/gql';

import {
  PriceListListOutput,
  ViewPriceListQuery,
  ListHotelsQuery,
  ListProductBundlesQuery,
  ListFacilitiesQuery,
  ListProductsQuery,
  ListPriceListsQuery,
  EPricingRuleType,
  EPricingRoundingType,
} from '__generated__/graphql';
import { formatDate } from 'components/DateTime';
import { allCurrencies } from 'languages';

import PriceSelectorView, { PriceSelectorCopyButton, PriceSelectorDeleteButton, PriceSelectorCreate as PriceSelectorCreateView } from './selector';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { SimpleDropdown } from 'components/form/FormInputDropdown';
import { RedirectError } from 'pages/error';
import { FACILITIES_LIST_QUERY, PRODUCTBUNDLES_LIST_QUERY, PRODUCTS_LIST_QUERY } from 'pages/products/gql';
import { GroupedSpaceSelectionInput, buildGroupedSpaceSelectionOptions } from 'components/security/GroupedSpaceSelectionInput';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { formatDocumentTitle } from 'helper/usedocumenttitle';

interface PriceListProps {
  id: number;
}
interface PriceListCreateProps { }
interface PriceListFormProps {
  data: NonNullable<ViewPriceListQuery['viewPriceList']>;
  priceLists: ListPriceListsQuery['listPriceLists']['priceLists'];
  bundles: ListProductBundlesQuery['listProductBundles']['bundles'];
  facilities: ListFacilitiesQuery['listFacilities']['facilities'];
  products: ListProductsQuery['listProducts']['products'];
  hotels: ListHotelsQuery['listHotels']['hotels'];
}

const validitySchema = yup.object().shape({
  validFrom: yup.date().nullable(),
  validTo: yup.date().nullable(),
});

const rulesSchema = yup.object().shape({
  rule: yup.mixed<EPricingRuleType>().oneOf(Object.values(EPricingRuleType)).required().label(i18next.t('pricelist-rules-rule')),
  rate: yup
    .number()
    .required()
    .label(i18next.t('pricelist-rules-rate'))
    .transform(value => (Number.isNaN(value) ? null : value)),
  rounding: yup.mixed<EPricingRoundingType>().oneOf(Object.values(EPricingRoundingType)).required().label(i18next.t('pricelist-rules-rounding')),
  productId: yup.number().nullable(),
  facilityId: yup.number().nullable(),
  bundleId: yup.number().nullable(),
});

const validationSchema = yup.object().shape({
  spaceId: yup.number().required().label(i18next.t('field-space')),
  name: yup.string().required().label(i18next.t('pricelist-name')),
  version: yup.string().required().label(i18next.t('pricelist-version')),
  refCode: yup.string().nullable(),
  isDefault: yup.boolean().required(),
  isPublished: yup.boolean().required(),
  isPricesBrut: yup.boolean().required(),
  currency: yup.string().required(),
  forLister: yup.boolean().required(),
  forWidget: yup.boolean().required(),
  hotelId: yup.number().nullable(),
  parentPriceListId: yup.number().required(),
  validity: yup.array().of(validitySchema),
  rules: yup.array().of(rulesSchema),
});

function PriceListForm(props: PriceListFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();

  const [activeSelectorId, setActiveSelectorId] = useState<number | 'create' | null>(null);

  const updateLocationHash = (hash: string | null, replace = true) => {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: hash ?? '',
      },
      { replace },
    );
  };

  const openSelector = (id: number, hash?: string) => {
    if (hash) {
      updateLocationHash(hash, false);
    } else {
      updateLocationHash(null);
    }
    setActiveSelectorId(id);
  };

  const openSelectorCreate = () => {
    updateLocationHash(null);
    setActiveSelectorId('create');
  };

  const closeSelector = () => {
    if (location.hash) {
      updateLocationHash(null);
    }
    setActiveSelectorId(null);
  };

  const renderSelectorLink = (label: React.ReactNode, selectorId: number, hash?: string) => (
    <Button variant="text" sx={{ padding: 0, minWidth: 0, textTransform: 'none' }} onClick={() => openSelector(selectorId, hash)}>
      {label}
    </Button>
  );

  const handleSelectorDeleted = (deletedId: number) => {
    if (activeSelectorId === deletedId) {
      closeSelector();
    }
  };

  const [updateMutateFunction] = useMutation(UPDATE_PRICELIST_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_PRICELIST_MUTATION);
  const [exportMutateFunction] = useMutation(EXPORT_PRICES_MUTATION);

  const [selectorsQuery, { data: selectorsData, loading: selectorsLoading, error: selectorsError }] = useLazyQuery(PRICESELECTOR_LIST_QUERY);
  const [hotelPriceListsQuery, { data: hotelPriceListsData, loading: hotelPriceListsLoading }] = useLazyQuery(HOTEL_PRICELISTS_QUERY);
  const [hotelQuery, { data: hotelData }] = useLazyQuery(HOTEL_WITH_SPACE_DETAILS_QUERY);
  const [priceListsQuery, { data: priceListsData }] = useLazyQuery(PRICELISTS_LIST_QUERY);

  const isCreatingSelector = activeSelectorId === 'create';
  const activeSelectorNumericId = typeof activeSelectorId === 'number' ? activeSelectorId : null;
  const currentSelector =
    activeSelectorNumericId !== null
      ? (selectorsData?.listPriceSelectors?.priceSelectors || []).find(selector => selector.id === activeSelectorNumericId) || null
      : null;


  const [lastFilterHotelId, setLastFilterHotelId] = useState<number | null>(null);

  const user = userSelector()!;
  const canEdit = props.data.id < 0 || canEditAdminRecord(user, props.data);

  const [createdId, setCreatedId] = useState(0);
  useEffect(() => {
    if (createdId > 0) navigate(`/pricelists/pricelists/${createdId}`);
  }, [createdId]);

  useEffect(() => {
    setActiveSelectorId(null);
    if (location.hash) {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: '',
        },
        { replace: true },
      );
    }
  }, [props.data.id, location.pathname, location.search, location.hash, navigate]);

  // Load hotel price lists when a hotel is associated with this price list
  useEffect(() => {
    if (props.data.hotel?.id) {
      hotelPriceListsQuery({
        variables: { hotelId: props.data.hotel.id },
      });
    }
  }, [props.data.hotel?.id, hotelPriceListsQuery]);

  // Monitor filter changes and load appropriate data
  useEffect(() => {
    if (filter?.hotelId) {
      // Load hotel details
      hotelQuery({
        variables: { id: filter.hotelId },
      });

      // Load price lists for the space/hotel
      priceListsQuery({
        variables: {
          spaceId: filter.spaceId || null,
          hotelId: filter.hotelId,
          isPublished: true,
          skip: 0,
          take: 100,
        },
      });
    }
  }, [filter?.hotelId, filter?.spaceId, hotelQuery, priceListsQuery]);

  // Auto-navigate to appropriate pricelist when filter data is available
  // Only navigate when hotel filter actually changes, not on manual toggles
  // Do not redirect while creating a new price list (id < 0)
  // Only auto-navigate in Epilo mode - in SEM mode users should manually select pricelists
  useEffect(() => {
    if (
      props.data.id >= 0 &&
      filter?.hotelId &&
      filter.hotelId !== lastFilterHotelId &&
      priceListsData?.listPriceLists?.priceLists &&
      hotelData?.viewHotel
    ) {
      const hotel = hotelData.viewHotel;
      const space = hotel.space;
      const pricelists = priceListsData.listPriceLists.priceLists;

      // Only auto-navigate in Epilo mode
      if (bookingMode === EBookingMode.EPILO) {
        let targetPricelist = null;

        if (space.hasEpilo) {
          // @ts-ignore
          targetPricelist = pricelists.find((pl: any) => pl.isPublished && pl.forLister);
        }

        if (targetPricelist && targetPricelist.id !== props.data.id) {
          navigate(`/pricelists/pricelists/${targetPricelist.id}`);
        }
      }

      // Update the tracked filter hotel ID (for both modes)
      setLastFilterHotelId(filter.hotelId);
    }
  }, [filter?.hotelId, lastFilterHotelId, priceListsData, hotelData, navigate, props.data.id, bookingMode]);

  // Auto-navigate to appropriate pricelist when booking mode changes
  // Do not redirect while creating a new price list (id < 0)
  useEffect(() => {
    if (props.data.id < 0 || !bookingMode || !hotelPriceListsData?.listPriceLists?.priceLists) return;

    const currentType = getCurrentPriceListType();
    const desiredType = bookingMode === EBookingMode.EPILO ? 'epilo' : 'sem';

    if (currentType && desiredType !== currentType) {
      // When switching to SEM mode, navigate to pricelists list view
      if (desiredType === 'sem') {
        navigate('/pricelists/pricelists', { replace: true });
      } else {
        // When switching to Epilo mode, navigate to the Epilo pricelist
        const otherPriceList = getOtherPriceList();
        if (otherPriceList) {
          navigate(`/pricelists/pricelists/${otherPriceList.id}`, { replace: true });
        }
      }
    }
  }, [bookingMode, hotelPriceListsData, props.data.id, navigate]);

  // Auto-open base price selector in Epilo mode when there's only one rule
  useEffect(() => {
    if (
      bookingMode === EBookingMode.EPILO &&
      !selectorsLoading &&
      selectorsData?.listPriceSelectors?.priceSelectors &&
      selectorsData.listPriceSelectors.priceSelectors.length === 1 &&
      activeSelectorId === null
    ) {
      const selector = selectorsData.listPriceSelectors.priceSelectors[0];
      if (selector.isBasePrice) {
        openSelector(selector.id);
      }
    }
  }, [selectorsData, selectorsLoading, bookingMode, activeSelectorId, openSelector]);

  type PriceListFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: NonNullable<ViewPriceListQuery['viewPriceList']>): PriceListFormType => ({
    ...obj,
    spaceId: obj.space.id,
    isPricesBrut: !obj.isPricesNet,
    hotelId: obj.hotel ? obj.hotel.id : 0,
    parentPriceListId: obj.parent ? obj.parent.id : 0,
    validity: obj.validity.map(v => ({
      validFrom: v.validFrom ? new Date(v.validFrom) : null,
      validTo: v.validTo ? new Date(v.validTo) : null,
    })),
    rules: obj.rules.map(r => ({
      rule: r.rule,
      rate: r.rule === EPricingRuleType.PERCENTAGE ? Math.floor(r.rate * 100) : r.rate,
      rounding: r.rounding,
      productId: r.product ? r.product.id : null,
      facilityId: r.facility ? r.facility.id : null,
      bundleId: r.bundle ? r.bundle.id : null,
    })),
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client: useApolloClient() },
    defaultValues: toFormSchema((props.data || {}) as PriceListListOutput),
  });

  const {
    fields: validityFields,
    append: validityAppend,
    remove: validityRemove,
  } = useFieldArray({
    control,
    name: 'validity',
  });
  const {
    fields: rulesFields,
    append: rulesAppend,
    remove: rulesRemove,
  } = useFieldArray({
    control,
    name: 'rules',
  });
  useEffect(() => {
    trigger();
  }, [trigger]);

  const getCurrentPriceListType = () => {
    if (props.data.forLister) return 'epilo';
    if (props.data.forWidget) return 'sem';
    return null;
  };

  const getOtherPriceList = () => {
    if (!hotelPriceListsData?.listPriceLists?.priceLists) return null;

    const currentType = getCurrentPriceListType();
    if (!currentType) return null;

    const targetType = currentType === 'epilo' ? 'sem' : 'epilo';

    // @ts-ignore
    return hotelPriceListsData.listPriceLists.priceLists.find((pl: any) => {
      if (targetType === 'epilo') return pl.forLister && !pl.forWidget;
      if (targetType === 'sem') return pl.forWidget && !pl.forLister;
      return false;
    });
  };

  const getPriceListTitle = () => {
    if (!props.data.hotel) return props.data.name;

    const currentType = getCurrentPriceListType();
    const typeText = currentType === 'epilo' ? 'seminargo' : currentType === 'sem' ? 'SEM' : '';

    if (typeText) {
      return i18next.t('pricelist-title-for-hotel-with-type', {
        type: typeText,
        hotel: props.data.hotel.name,
      });
    }

    return i18next.t('pricelist-title-for-hotel', {
      hotel: props.data.hotel.name,
    });
  };

  const onSubmit = async (values: PriceListFormType) => {
    try {
      if (props.data.id > 0) {
        const res = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: {
              name: values.name,
              version: values.version,
              isDefault: bookingMode === EBookingMode.SEM ? true : false,
              isPublished: values.isPublished,
              isPricesNet: !values.isPricesBrut,
              currency: values.currency,
              forLister: values.forLister,
              forWidget: values.forWidget,
              hotelId: values.hotelId && values.hotelId > 0 ? values.hotelId : null,
              parentPriceListId: values.parentPriceListId && values.parentPriceListId > 0 ? values.parentPriceListId! : null,
              validity: values.validity || [],
              rules: (values.rules || []).map(r => ({
                ...r,
                rate: r.rule === EPricingRuleType.PERCENTAGE ? r.rate / 100 : r.rate,
              })),
            },
          },
          update: cache => EVICT_PRICELISTS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_PRICELISTS_QUERIES(props.data.id),
        });
        reset(toFormSchema((res.data!.updatePriceList || {}) as PriceListListOutput));
      } else {
        const res = await createMutateFunction({
          variables: {
            hotelId: values.hotelId && values.hotelId > 0 ? values.hotelId! : 0,
            spaceId: values.spaceId,
            data: {
              name: values.name,
              version: values.version,
              isDefault: bookingMode === EBookingMode.SEM ? true : false,
              isPricesNet: !values.isPricesBrut,
              currency: values.currency,
              forLister: values.forLister,
              forWidget: values.forWidget,
              parentPriceListId: values.parentPriceListId && values.parentPriceListId > 0 ? values.parentPriceListId! : null,
              validity: values.validity || [],
              rules: (values.rules || []).map(r => ({
                ...r,
                rate: r.rule === EPricingRuleType.PERCENTAGE ? r.rate / 100 : r.rate,
              })),
            },
          },
          update: cache => EVICT_PRICELISTS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_PRICELISTS_QUERIES(props.data.id),
        });
        reset(toFormSchema((res.data!.createPriceList || {}) as PriceListListOutput));
        setCreatedId(res.data!.createPriceList.id);
      }
      dispatchMessage(dispatch, i18next.t('pricelist-saved'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const _renderRuleScope = (index: number) => {
    const row = getValues(`rules.${index}`);
    if (row && row.productId) {
      const product = props.products.find(p => p.id === row.productId);

      return (
        <Link to={`/products/products/${row.productId}`}>
          {product?.name}
          {product?.extRefCode && ` (${product?.extRefCode})`}
        </Link>
      );
    }
    if (row && row.bundleId) {
      const bundle = props.bundles.find(p => p.id === row.bundleId);

      return (
        <Link to={`/products/bundles/${row.bundleId}`}>
          {bundle?.name}
          {bundle?.extRefCode && ` (${bundle?.extRefCode})`}
        </Link>
      );
    }
    if (row && row.facilityId) {
      const facility = props.facilities.find(p => p.id === row.facilityId);

      return (
        <Link to={`/products/facilities/${row.facilityId}`}>
          {facility?.name}
          {facility?.extRefCode && ` (${facility?.extRefCode})`}
        </Link>
      );
    }
    return <>{i18next.t('pricelist-rules-all')}</>;
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([getPriceListTitle(), props.data])}</title>
      </Helmet>

      {/* Price List Header */}
      <Grid container spacing={3} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Typography variant="h6" component="div">
              {props.data.hotel ? (
                getPriceListTitle()
              ) : (
                getPriceListTitle()
              )}
            </Typography>
            {props.data.refCode && (
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                {props.data.refCode}
              </Typography>
            )}
          </div>
          {props.data.id > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={watch('isPricesBrut')}
                  onChange={async e => {
                    const newValue = e.target.checked;
                    setValue('isPricesBrut', newValue, { shouldDirty: true });
                    // Auto-save after changing the switch
                    const valid = await trigger();
                    if (valid) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                  disabled={!canEdit}
                />
              }
              label={i18next.t('pricelist-ispricesbrut')}
              labelPlacement="start"
            />
          )}
        </Grid>
      </Grid>

      {/* Show full form when creating a new price list or for SEM */}
      {(props.data.id < 0 || bookingMode === EBookingMode.SEM) && (
        <Grid container spacing={3}>
          <UnsavedChangesPrompt isDirty={isDirty} />
          <Grid item xs={12} sm={6}>
            <FormInputText name="name" control={control} label={i18next.t('pricelist-name')} required disabled={!canEdit} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SpaceSelectionInput
              checkAdmin
              name="spaceId"
              control={control}
              disabled={!canEdit || props.data.id > 0 || user.isSingleAdminSpace}
              required
              useFilterSpaceForRoot
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInputDropdown
              name="currency"
              control={control}
              label={i18next.t('pricelist-currency')}
              options={[...allCurrencies.map(c => (c ? { value: c.code, label: c.label } : ({ divider: true } as any)))]}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInputText name="refCode" control={control} label={i18next.t('pricelist-refcode')} disabled />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInputText name="version" control={control} label={i18next.t('pricelist-version')} disabled={!canEdit} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <HotelSelectionInput
              name="hotelId"
              control={control}
              label={i18next.t('pricelist-hotel')}
              hotels={props.hotels}
              emptyLabel={i18next.t('pricelist-hotel-none')}
              emptyValue={0}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInputCheckbox
              name="isPublished"
              control={control}
              label={i18next.t('pricelist-ispublished')}
              disabled={!canEdit || props.data.id < 0}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" component="div">
              {i18next.t('pricelist-validity')}
            </Typography>
          </Grid>
          {validityFields.map((field, index) => (
            <React.Fragment key={field.id}>
              <Grid item xs={12} sm={4}>
                <FormInputDate name={`validity.${index}.validFrom`} control={control} label={i18next.t('pricelist-validfrom')} disabled={!canEdit} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormInputDate name={`validity.${index}.validTo`} control={control} label={i18next.t('pricelist-validto')} disabled={!canEdit} />
              </Grid>
              {canEdit && (
                <Grid item xs={12} sm={4}>
                  <IconButton onClick={() => validityRemove(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              )}
            </React.Fragment>
          ))}
          {canEdit && (
            <Grid item xs={12}>
              <Button startIcon={<AddIcon />} onClick={() => validityAppend({ validFrom: null, validTo: null })}>
                {i18next.t('pricelist-validity-add')}
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            {canEdit && (
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating}
                onClick={async () => {
                  const valid = await trigger();
                  if (valid) {
                    handleSubmit(onSubmit)();
                  }
                }}
              >
                {i18next.t('pricelist-save')}
              </Button>
            )}
            {canEdit && props.data.id > 0 && <PriceListDeleteButton id={props.data.id} spaceId={props.data.space.id} icon={false} />}
            {props.data.id > 0 && (
              <PriceListCopyButton id={props.data.id} spaceId={props.data.space.id} hotelId={props.data.hotel?.id || null} icon={false} />
            )}
            {props.data.id > 0 && (
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                color="secondary"
                startIcon={<ImportExportIcon />}
                onClick={async () => {
                  try {
                    const res = await exportMutateFunction({
                      variables: {
                        priceListId: props.data.id,
                      },
                    });
                    if (res.data?.exportPriceList) {
                      exportToFile(res.data?.exportPriceList, 'application/json', 'seminargo-prices.json');
                    }
                    dispatchMessage(dispatch, i18next.t('prices-export-ready'));
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                {i18next.t('prices-export-run')}
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      {/* Divider between validity and price selectors - only in SEM mode */}
      {props.data.id > 0 && bookingMode === EBookingMode.SEM && <Divider sx={{ my: 4 }} />}

      {/* Preisregel (Selektoren) - direkt ohne Tabs */}
      {props.data.id > 0 && (
        <>
          <PagedTable
            filterKey={`${props.data.id}-${bookingMode}`}
            refetchPage={(skip, take) =>
              selectorsQuery({
                variables: {
                  priceListId: props.data.id,
                  skip,
                  take,
                },
              })
            }
            rowsLoading={selectorsLoading}
            rowsErr={selectorsError}
            headers={[
              i18next.t('pricelist-selector-name'),
              i18next.t('pricelist-selector-baseprice'),
              i18next.t('pricelist-selector-minguestcount'),
              i18next.t('pricelist-selector-maxguestcount'),
              i18next.t('pricelist-selector-minitemcount'),
              i18next.t('pricelist-selector-maxitemcount'),
              i18next.t('pricelist-selector-validity'),
              i18next.t('pricelist-selector-weekdays'),
              '',
            ]}
            rows={(selectorsData?.listPriceSelectors?.priceSelectors || []).map(selector => [
              renderSelectorLink(
                selector.isBasePrice ? i18next.t('pricelist-selector-baseprice') : selector.name,
                selector.id,
              ),
              selector.isBasePrice && <CheckIcon />,
              selector.minGuestCount,
              selector.maxGuestCount,
              selector.minItemCount,
              selector.maxItemCount,
              (selector.validity || []).map((v, vindex) => (
                <React.Fragment key={vindex}>
                  {v.validFrom ? formatDate(v.validFrom) : '*'} - {v.validTo ? formatDate(v.validTo) : '*'}
                  <br />
                </React.Fragment>
              )),
              selector.weekdays && selector.weekdays.join(', '),
              <ButtonGroup>
                <PriceSelectorCopyButton id={selector.id} priceListId={selector.priceList.id} spaceId={selector.space.id} icon />
                {!selector.isBasePrice && (
                  <PriceSelectorDeleteButton
                    id={selector.id}
                    priceListId={selector.priceList.id}
                    spaceId={selector.space.id}
                    icon
                    onBeforeDelete={() => {
                      // Close inline form before delete to prevent stale query refetch errors
                      if (activeSelectorId === selector.id) {
                        closeSelector();
                      }
                    }}
                    onDeleted={() => handleSelectorDeleted(selector.id)}
                  />
                )}
              </ButtonGroup>,
            ])}
            totalCount={selectorsData?.listPriceSelectors?.totalCount}
          />
          {canEdit && (
            <Box sx={{ marginTop: 2 }}>
              <Button startIcon={<AddIcon />} onClick={openSelectorCreate}>
                {i18next.t('pricelist-selector-add')}
              </Button>
            </Box>
          )}
        </>
      )}
      {activeSelectorId && (
        <Paper elevation={0} sx={{ mt: 4, p: 3, border: '1px solid', borderColor: 'divider' }}>
          <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Grid item>
              <Typography variant="h6">
                {isCreatingSelector
                  ? i18next.t('pricelist-selector-add')
                  : currentSelector?.isBasePrice
                    ? i18next.t('pricelist-selector-baseprice')
                    : currentSelector?.name || i18next.t('selector-name')}
              </Typography>
            </Grid>
            <Grid item>
              <Button variant="text" onClick={closeSelector}>
                {i18next.t('close')}
              </Button>
            </Grid>
          </Grid>
          {isCreatingSelector ? (
            <PriceSelectorCreateView priceListId={props.data.id} spaceId={props.data.space.id} forLister={props.data.forLister} priceList={props.data as PriceListListOutput} inline onNavigateToSelector={openSelector} />
          ) : (
            activeSelectorNumericId !== null && (
              <PriceSelectorView
                priceListId={props.data.id}
                spaceId={props.data.space.id}
                forLister={props.data.forLister}
                id={activeSelectorNumericId}
                inline
                onNavigateToSelector={openSelector}
                onDeleted={() => handleSelectorDeleted(activeSelectorNumericId)}
              />
            )
          )}
        </Paper>
      )}
    </>
  );
}

/**
 * Price list detail page wrapper that loads pricelist data.
 * Renders PriceListForm for editing pricelist configuration, prices, and validation.
 */
export default function PriceList(props: PriceListProps) {
  const priceListQuery = useQuery(PRICELIST_VIEW_QUERY, {
    variables: { id: props.id },
  });

  // Extract spaceId from the price list for filtering
  const spaceId = priceListQuery.data?.viewPriceList?.space?.id;

  const pricelistsQuery = useQuery(PRICELISTS_LIST_QUERY, {
    variables: { spaceId },
    skip: !spaceId,
  });
  const productsQuery = useQuery(PRODUCTS_LIST_QUERY, {
    variables: { spaceId },
    skip: !spaceId,
  });
  const bundlesQuery = useQuery(PRODUCTBUNDLES_LIST_QUERY, {
    variables: { spaceId },
    skip: !spaceId,
  });
  const facilitiesQuery = useQuery(FACILITIES_LIST_QUERY, {
    variables: { spaceId },
    skip: !spaceId,
  });
  const hotelsQuery = useQuery(HOTEL_LIST_QUERY, {
    variables: { spaceId },
    skip: !spaceId,
  });

  const loading =
    priceListQuery.loading;
  const error =
    priceListQuery.error || pricelistsQuery.error || productsQuery.error || bundlesQuery.error || facilitiesQuery.error || hotelsQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else
    return (
      <PriceListForm
        data={priceListQuery.data!.viewPriceList!}
        priceLists={pricelistsQuery.data?.listPriceLists?.priceLists || []}
        products={productsQuery.data?.listProducts?.products || []}
        facilities={facilitiesQuery.data?.listFacilities?.facilities || []}
        bundles={bundlesQuery.data?.listProductBundles?.bundles || []}
        hotels={hotelsQuery.data?.listHotels?.hotels || []}
      />
    );
}

/**
 * Price list creation page for new pricelists.
 * Uses PriceListForm with empty initial data.
 */
export function PriceListCreate(props: PriceListCreateProps) {
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();
  const user = userSelector()!;

  // Use spaceId from filter for filtering data
  const spaceId = filter?.spaceId;

  // Set default values based on booking mode
  // SEM mode: forWidget=true, forLister=false
  // Epilo mode: forWidget=false, forLister=true
  const isSemMode = bookingMode === EBookingMode.SEM;
  const defaultForWidget = isSemMode;
  const defaultForLister = !isSemMode;

  const pricelistsQuery = useQuery(PRICELISTS_LIST_QUERY, {
    variables: { spaceId },
  });
  const productsQuery = useQuery(PRODUCTS_LIST_QUERY, {
    variables: { spaceId },
  });
  const bundlesQuery = useQuery(PRODUCTBUNDLES_LIST_QUERY, {
    variables: { spaceId },
  });
  const facilitiesQuery = useQuery(FACILITIES_LIST_QUERY, {
    variables: { spaceId },
  });
  const hotelsQuery = useQuery(HOTEL_LIST_QUERY, {
    variables: { spaceId },
  });

  const loading = pricelistsQuery.loading || productsQuery.loading || bundlesQuery.loading || facilitiesQuery.loading || hotelsQuery.loading;
  const error = pricelistsQuery.error || productsQuery.error || bundlesQuery.error || facilitiesQuery.error || hotelsQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else
    return (
      <PriceListForm
        data={{
          id: -1,
          name: '',
          version: moment().format('YYYY-DD-MM HH:mm:ss'),
          refCode: '',
          isDefault: isSemMode ? true : false,
          isPublished: false,
          isPricesNet: false,
          forLister: defaultForLister,
          forWidget: defaultForWidget,
          hotel: null,
          currency: 'EUR',
          validity: [],
          rules: [],
          children: [],
          space: { id: initialSpaceId(user, filter) } as any,
        }}
        priceLists={pricelistsQuery.data!.listPriceLists.priceLists}
        products={productsQuery.data!.listProducts.products}
        facilities={facilitiesQuery.data!.listFacilities.facilities}
        bundles={bundlesQuery.data!.listProductBundles.bundles}
        hotels={hotelsQuery.data!.listHotels.hotels}
      />
    );
}

interface PriceListCopyButtonProps {
  id: number;
  spaceId: number;
  hotelId: number | null;
  icon: boolean;
}
export function PriceListCopyButton(props: PriceListCopyButtonProps) {
  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [spaceId, setSpaceId] = useState<number>(props.spaceId);

  const user = userSelector()!;
  const canEdit = user.isSeminargo || (user.isAdmin && user.space);

  const [copyMutateFunction, { loading: copyMutateLoading }] = useMutation(COPY_PRICELIST_MUTATION);

  const __do = async () => {
    if (!canEdit) return;
    try {
      const res = await copyMutateFunction({
        variables: { id: props.id, hotelId: props.hotelId || 0 },
        update: cache => EVICT_PRICELISTS_QUERIES(cache),
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_PRICELISTS_QUERIES(),
      });
      dispatchMessage(dispatch, i18next.t('pricelist-copied'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  if (props.icon && !canEdit) return null;

  if (props.icon) {
    return (
      <>
        <IconButton disabled={!canEdit || copyMutateLoading} onClick={() => (user.isSingleSpace ? __do() : setDialogOpen(true))}>
          <Tooltip title={i18next.t('pricelist-copy')}>
            <ContentCopyIcon />
          </Tooltip>
        </IconButton>
        <ConfirmationDialog open={dialogOpen} setOpen={setDialogOpen} title={i18next.t('pricelist-confirm-copy-title')} onConfirm={__do}>
          <SimpleDropdown
            name="spaceId"
            onChange={v => setSpaceId(v)}
            options={buildSpaceSelectionOptions(user, true)}
            label={i18next.t('pricelist-space')}
            value={spaceId}
          />
        </ConfirmationDialog>
      </>
    );
  } else {
    return (
      <>
        <Button
          sx={{ marginRight: 2 }}
          variant="contained"
          color="secondary"
          disabled={!canEdit || copyMutateLoading}
          startIcon={copyMutateLoading ? <CircularProgress size={24} /> : <ContentCopyIcon />}
          onClick={() => (user.isSingleSpace ? __do() : setDialogOpen(true))}
        >
          {i18next.t('pricelist-copy')}
        </Button>
        <ConfirmationDialog open={dialogOpen} setOpen={setDialogOpen} title={i18next.t('pricelist-confirm-copy-title')} onConfirm={__do}>
          <SimpleDropdown
            name="spaceId"
            onChange={v => setSpaceId(v)}
            options={buildSpaceSelectionOptions(user, true)}
            label={i18next.t('pricelist-space')}
            value={spaceId}
          />
        </ConfirmationDialog>
      </>
    );
  }
}

interface PriceListDeleteButtonProps {
  id: number;
  spaceId: number;
  icon: boolean;
}
export function PriceListDeleteButton(props: PriceListDeleteButtonProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = userSelector()!;
  const canEdit = canEditAdminSpaceId(user, props.spaceId);

  const [deleteMutateFunction, { loading: deleteMutateLoading }] = useMutation(DELETE_PRICELIST_MUTATION);

  if (props.icon && !canEdit) return null;

  return (
    <ConfirmationButton
      sx={{ marginRight: 2 }}
      disabled={!canEdit || deleteMutateLoading}
      icon={props.icon}
      {...(props.icon
        ? {}
        : {
          startIcon: deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />,
          variant: 'contained',
          color: 'secondary',
        })}
      confirmationQuestion={i18next.t('pricelist-confirm-delete')}
      confirmationTitle={i18next.t('pricelist-confirm-delete-title')}
      onConfirm={async () => {
        if (!canEdit) return;
        try {
          const res = await deleteMutateFunction({
            variables: {
              id: props.id,
            },
            update: cache => EVICT_PRICELISTS_QUERIES(cache),
            awaitRefetchQueries: true,
            refetchQueries: REFETCH_PRICELISTS_QUERIES(),
          });
          navigate('/pricelists/pricelists');
          dispatchMessage(dispatch, i18next.t('pricelist-deleted'));
        } catch (err) {
          dispatchException(dispatch, err);
        }
      }}
    >
      {props.icon && (deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />)}
      {!props.icon && i18next.t('pricelist-delete')}
    </ConfirmationButton>
  );
}
